import { afterNextRender, Component, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { CustomerComponentState } from '../../shared/state-interface';
import { CountriesComponent } from '../../ui-components/countries/countries.component';
import { Customer } from './customer.model';

@Component({
    selector: 'app-customer-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        CountriesComponent
    ],
    templateUrl: './customer.component.html',
    styleUrl: './customer.component.css'
})
export class CustomerComponent {

    @ViewChild(CountriesComponent) countriesComponent!:CountriesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private elementRef: ElementRef = inject(ElementRef);

    public customerForm = new FormGroup({
        firstName: new FormControl(""),
        lastName: new FormControl(""),
        businessName: new FormControl(""),
        email: new FormControl(""),
        phone: new FormControl(""),
        addressLine1: new FormControl(""),
        addressLine2: new FormControl(""),
        addressLine3: new FormControl("")
    });

    public constructor() {
        afterNextRender(() => {
            // This LifeCycle hook primarily supports the "Add" Use Case and
            // the browser back button Use Case. The setTimeout is necessary
            // to put these commands at the end of the event queue in attempt
            // to delay their invocation until the DOM is fully updated. Same
            // functions are invoked from the populateFormFromModel function
            // to support the "Update" Use Case.
            setTimeout(() => {
                const ref = this.elementRef.nativeElement.querySelector("input");
                ref?.focus();
                ref?.select();
            }, 0);
        });
    }

    public getState(): CustomerComponentState {
        const state: CustomerComponentState = {} as CustomerComponentState
        state.customerForm = this.customerForm;
        state.countriesComponentState = this.countriesComponent.getState();
        return state;
    }

    public restoreState(state: CustomerComponentState) {
        this.customerForm = state.customerForm;
        this.countriesComponent.restoreState(state.countriesComponentState);
    }

    public init(countryCode?: string) {
        this.countriesComponent.countryCode = countryCode;
        this.countriesComponent.init();
    }

    public receiveMessage(value: string) {
        this.messageEvent.emit(value);
    }

    public populateFormFromModel(customer: Customer) {
        this.firstNameCtl.setValue(customer.first_name);
        this.lastNameCtl.setValue(customer.last_name);
        this.businessNameCtl.setValue(customer.business_name);
        this.emailCtl.setValue(customer.email);
        this.phoneCtl.setValue(customer.phone);
        this.addressLine1Ctl.setValue(customer.address_line_1);
        this.addressLine2Ctl.setValue(customer.address_line_2);
        this.addressLine3Ctl.setValue(customer.address_line_3 ? customer.address_line_3 : "");
        this.countriesComponent.countryCode = customer.country_code;

        const ref = this.elementRef.nativeElement.querySelector("input");
        ref?.focus();
        ref?.select();
    }

    public createModelFromForm(customer: Customer): Customer {
        customer.first_name = this.firstNameCtl.value;
        customer.last_name = this.lastNameCtl.value;
        customer.business_name = this.businessNameCtl.value;
        customer.email = this.emailCtl.value;
        customer.phone = this.phoneCtl.value;
        customer.address_line_1 = this.addressLine1Ctl.value;
        customer.address_line_2 = this.addressLine2Ctl.value;
        customer.address_line_3 = this.addressLine3Ctl.value;
        customer.country_code = this.countriesComponent.selectedCountry;
        return customer;
    }

    private get lastNameCtl(): FormControl {
        return this.customerForm.controls.lastName;
    }

    private get firstNameCtl(): FormControl {
        return this.customerForm.controls.firstName;
    }

    private get businessNameCtl(): FormControl {
        return this.customerForm.controls.businessName;
    }

    private get emailCtl(): FormControl {
        return this.customerForm.controls.email;
    }

    private get phoneCtl(): FormControl {
        return this.customerForm.controls.phone;
    }

    private get addressLine1Ctl(): FormControl {
        return this.customerForm.controls.addressLine1;
    }

    private get addressLine2Ctl(): FormControl {
        return this.customerForm.controls.addressLine2;
    }

    private get addressLine3Ctl(): FormControl {
        return this.customerForm.controls.addressLine3;
    }
}